import { z } from "zod";
// Per-app env schemas. Each app validates only the vars it actually uses, so a
// missing secret fails loud at the app that needs it — and an app can't read a
// secret it has no business touching (the worker, e.g., never sees WORKOS_*).
// Composed from a shared base plus opt-in blocks.
// Shared by every app.
const baseSchema = z.object({
    NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
    PORT: z.coerce.number().default(8080),
    APP_BASE_URL: z.string().url().default("http://localhost:5173"),
    DATABASE_URL: z.string().min(1),
    // Cloud SQL instance connection name (project:region:instance). When set, the
    // DB layer routes through the Cloud SQL Node Connector instead of a plain
    // connectionString — see packages/db/src/connection-config.ts. Optional:
    // unset for local docker; set in every deployed environment (prod/staging/dev).
    // min(1) rejects an empty string at startup — the runtime truthy checks in
    // packages/db would silently treat "" as unset, hiding a typoed deploy config.
    INSTANCE_CONNECTION_NAME: z.string().min(1).optional(),
    GOOGLE_APPLICATION_CREDENTIALS: z.string().optional(),
    GOOGLE_CLOUD_PROJECT: z.string().optional(),
});
// Outbound notifications (api + worker). All optional — SendGrid is deferred
// (TEL-69) and a partial/missing config must never block startup; the app
// simply skips email (auth emails go out via WorkOS) until both halves are set.
const notificationsSchema = z.object({
    SENDGRID_API_KEY: z.string().optional(),
    SENDGRID_FROM_EMAIL: z.string().email().optional(),
    TWILIO_ACCOUNT_SID: z.string().optional(),
    TWILIO_AUTH_TOKEN: z.string().optional(),
    TWILIO_FROM_NUMBER: z.string().optional(),
    // Fallback admin email for TEL-17-D interim period (before listPlatformAdmins is available).
    ADMIN_ALERT_EMAIL: z.string().email().optional(),
});
// Attachments pipeline (TEL-33 + TEL-79). Optional here; both the scanner
// (apps/scanner/src/config.ts) and the api (apps/api/src/config/attachments.ts)
// re-validate them as required at their own startup.
const attachmentsSchema = z.object({
    ATTACHMENT_UPLOAD_BUCKET: z.string().optional(),
    ATTACHMENT_CLEAN_BUCKET: z.string().optional(),
    ATTACHMENT_INFECTED_BUCKET: z.string().optional(),
    ATTACHMENT_PROCESSED_BUCKET: z.string().optional(),
    // Dedicated bucket for system-generated consult PDFs (TEL-110). Distinct from
    // the four-bucket scanner pipeline so artifacts never re-enter ClamAV and get
    // their own access logging. The API signs GET URLs against it; the worker
    // writes generated PDFs into it. Optional here; the api
    // (apps/api/src/config/attachments.ts) and worker
    // (apps/worker/src/config/buckets.ts) re-validate as required at startup.
    GENERATED_PDF_BUCKET: z.string().optional(),
    // Dedicated bucket for the specialist handbook PDF (TEL-219). A trusted-staff
    // published, non-PHI contract document — outside the scanner pipeline. The
    // API signs GET URLs against it. Optional here; the api
    // (apps/api/src/config/handbook.ts) re-validates as required on first use.
    HANDBOOK_BUCKET: z.string().optional(),
});
// MFA dev-bypass allowlist (TEL-112). Reads the RAW process.env.NODE_ENV — NOT
// the Zod-parsed value, which defaults an unset NODE_ENV to "development" and
// would silently open the backdoor on a deployed runtime that forgot to set it.
// The single source of truth for "may the fixed bypass code ever be honored?",
// consulted both at startup (below) and at request time in the MFA service.
export function isMfaDevBypassAllowed() {
    const raw = process.env.NODE_ENV;
    return raw === "development" || raw === "test";
}
// Rate-limit dev/e2e escape hatch (TEL-237). Same raw-NODE_ENV posture as
// isMfaDevBypassAllowed above: an unset NODE_ENV must not be treated as
// "development", or a deployed runtime that forgot to set it could silently
// ship with abuse limits off.
export function isRateLimitDisableAllowed() {
    const raw = process.env.NODE_ENV;
    return raw === "development" || raw === "test";
}
const apiSchema = baseSchema
    .merge(notificationsSchema)
    .merge(attachmentsSchema)
    .extend({
    GCS_BUCKET: z.string().optional(),
    // Prod-only CSP hardening (TEL-127 A6). When "true", the api mints signed
    // URLs virtual-hosted-style (https://<bucket>.storage.googleapis.com) and
    // narrows the prod CSP to those per-bucket hosts instead of the broad
    // storage.googleapis.com origin. Unset / "false" = off (dev/staging keep
    // today's path-style behavior). Restricted to the two literals so a typo
    // ("ture") fails startup loudly instead of silently leaving prod
    // un-hardened; a blank value is coerced to undefined (same pattern as
    // MFA_DEV_BYPASS_CODE) so `ATTACHMENT_VIRTUAL_HOSTED=` in a .env file
    // doesn't crash startup.
    ATTACHMENT_VIRTUAL_HOSTED: z.preprocess((v) => (v === "" ? undefined : v), z.enum(["true", "false"]).optional()),
    // No WORKOS_REDIRECT_URI: every user-facing link is built from APP_BASE_URL
    // and all auth flows are API-only (TEL-206) — no WorkOS-hosted page ever
    // redirects back to us, so there is nothing for a redirect URI to control.
    WORKOS_API_KEY: z.string().min(1),
    WORKOS_CLIENT_ID: z.string().min(1),
    WORKOS_COOKIE_PASSWORD: z.string().min(32),
    // Local-dev / e2e only — never set in any deployed environment. Lets the MFA
    // flow accept a fixed 6-digit code without calling WorkOS or sending SMS.
    // A blank value (`.env.example` ships `MFA_DEV_BYPASS_CODE=`) is coerced to
    // undefined so an empty string doesn't fail the regex and crash startup.
    MFA_DEV_BYPASS_CODE: z.preprocess((v) => (v === "" ? undefined : v), z
        .string()
        .regex(/^[0-9]{6}$/)
        .optional()),
    // Local-dev / e2e only — never set in any deployed environment. Skips
    // registering the abuse rate limiter (TEL-237) so Playwright's repeated
    // UI logins from a single IP can't trip the login/MFA thresholds and flake
    // CI. Restricted to the two literals and coerced from "" like
    // ATTACHMENT_VIRTUAL_HOSTED above; only "true" disables anything.
    RATE_LIMIT_DISABLED: z.preprocess((v) => (v === "" ? undefined : v), z.enum(["true", "false"]).optional()),
})
    // Fail closed: refuse to boot if the bypass secret is present while the raw
    // NODE_ENV is not in the allowlist (production, an unknown value, or unset).
    // A deployed env that accidentally carries the secret crashes loudly instead
    // of silently enabling a login backdoor.
    .superRefine((env, ctx) => {
    if (env.MFA_DEV_BYPASS_CODE && !isMfaDevBypassAllowed()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["MFA_DEV_BYPASS_CODE"],
            message: "MFA_DEV_BYPASS_CODE may only be set when NODE_ENV is exactly 'development' or 'test' (an unset NODE_ENV is not allowed)",
        });
    }
    if (env.RATE_LIMIT_DISABLED === "true" && !isRateLimitDisableAllowed()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["RATE_LIMIT_DISABLED"],
            message: "RATE_LIMIT_DISABLED may only be 'true' when NODE_ENV is exactly 'development' or 'test' (an unset NODE_ENV is not allowed)",
        });
    }
});
// The worker reads the processed-attachments bucket (to embed image thumbnails)
// and writes to the generated-PDF bucket (TEL-110), so it now carries the
// attachments schema too.
const workerSchema = baseSchema.merge(notificationsSchema).merge(attachmentsSchema);
const scannerSchema = baseSchema.merge(attachmentsSchema).extend({
    SCANNER_PORT: z.coerce.number().default(8082),
});
function parseEnv(schema, app) {
    const parsed = schema.safeParse(process.env);
    if (!parsed.success) {
        const issues = parsed.error.issues
            .map((i) => `  - ${i.path.join(".")}: ${i.message}`)
            .join("\n");
        throw new Error(`Invalid ${app} environment configuration:\n${issues}`);
    }
    return parsed.data;
}
// Each loader caches independently after its first successful parse.
let cachedApi;
export function loadApiEnv() {
    return (cachedApi ??= parseEnv(apiSchema, "api"));
}
let cachedWorker;
export function loadWorkerEnv() {
    return (cachedWorker ??= parseEnv(workerSchema, "worker"));
}
let cachedScanner;
export function loadScannerEnv() {
    return (cachedScanner ??= parseEnv(scannerSchema, "scanner"));
}
//# sourceMappingURL=env.js.map